/**
 * Task Action Registry — static metadata for all task actions.
 *
 * Shared by Action Panel MoreMenu, context menus, and keyboard shortcuts.
 * Pure data — no React hooks, no side effects.
 *
 * Each surface picks which actions to show and in what order.
 * The registry provides consistent labels, icons, and shortcut hints.
 */

import Check from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import Undo2 from "@/shared/icons/totodoicons/stroke/undo.svg?react"
import Ban from "@/shared/icons/pikaicons/stroke/security/ban-left.svg?react"
import MoonStar from "@/shared/icons/totodoicons/stroke/moon.svg?react"
import Someday from "@/shared/icons/totodoicons/stroke/archive.svg?react"
import CalendarDays from "@/shared/icons/totodoicons/stroke/calendar.svg?react"
import Flag from "@/shared/icons/totodoicons/stroke/flag.svg?react"
import FolderInput from "@/shared/icons/pikaicons/stroke/files-folders/folder-arrow-right.svg?react"
import Tag from "@/shared/icons/totodoicons/stroke/tag.svg?react"
import CircleDot from "@/shared/icons/pikaicons/stroke/general/circle-dot.svg?react"
import FocusTarget from "@/shared/icons/pikaicons/stroke/general/focus-target.svg?react"
import Pin from "@/shared/icons/pikaicons/stroke/general/pin-slant.svg?react"
import Duplicate from "@/shared/icons/totodoicons/stroke/duplicate.svg?react"
import Copy from "@/shared/icons/totodoicons/stroke/copy.svg?react"
import Link from "@/shared/icons/pikaicons/stroke/development/link-slant.svg?react"
import Scissors from "@/shared/icons/pikaicons/stroke/editing/scissors-default.svg?react"
import Share2 from "@/shared/icons/totodoicons/stroke/share.svg?react"
import Heading from "@/shared/icons/totodoicons/stroke/section.svg?react"
import Project from "@/shared/icons/totodoicons/stroke/project.svg?react"
import Trash2 from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import type { TaskActionDef, TaskActionId } from "./types"

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const TASK_ACTIONS: Record<TaskActionId, TaskActionDef> = {
  complete: {
    id: "complete",
    label: "Complete",
    labelBulk: "Complete All",
    icon: Check,
    shortcut: "Mod+K",
    appliesTo: "both",
  },
  uncomplete: {
    id: "uncomplete",
    label: "Mark as Open",
    icon: Undo2,
    appliesTo: "both",
  },
  cancel: {
    id: "cancel",
    label: "Cancel Task",
    labelBulk: "Cancel All",
    icon: Ban,
    appliesTo: "task",
  },
  schedule: {
    id: "schedule",
    label: "When…",
    icon: CalendarDays,
    shortcut: "Mod+S",
    appliesTo: "task",
    requiresInput: true,
  },
  evening: {
    id: "evening",
    label: "This Evening",
    icon: MoonStar,
    shortcut: "Mod+E",
    appliesTo: "task",
  },
  someday: {
    id: "someday",
    label: "Someday",
    icon: Someday,
    shortcut: "Mod+O",
    appliesTo: "task",
  },
  deadline: {
    id: "deadline",
    label: "Set deadline",
    icon: Flag,
    shortcut: "Shift+Mod+D",
    appliesTo: "task",
    requiresInput: true,
  },
  move: {
    id: "move",
    label: "Move to…",
    icon: FolderInput,
    shortcut: "Shift+Mod+M",
    appliesTo: "both",
    requiresInput: true,
  },
  tags: {
    id: "tags",
    label: "Tags…",
    icon: Tag,
    shortcut: "Shift+Mod+T",
    appliesTo: "task",
    requiresInput: true,
  },
  "set-status": {
    id: "set-status",
    label: "Set Status",
    icon: CircleDot,
    shortcut: "Mod+Y",
    appliesTo: "task",
    requiresInput: true,
  },
  focus: {
    id: "focus",
    label: "In Focus",
    icon: FocusTarget,
    appliesTo: "task",
    singleOnly: true,
  },
  pin: {
    id: "pin",
    label: "Pin to Top",
    icon: Pin,
    appliesTo: "task",
    singleOnly: true,
  },
  duplicate: {
    id: "duplicate",
    label: "Duplicate",
    labelBulk: "Duplicate All",
    icon: Duplicate,
    shortcut: "Mod+D",
    appliesTo: "both",
  },
  "copy-to-clipboard": {
    id: "copy-to-clipboard",
    label: "Copy to Clipboard",
    icon: Copy,
    appliesTo: "both",
  },
  "copy-link": {
    id: "copy-link",
    label: "Copy Link",
    icon: Link,
    shortcut: "Mod+Shift+C",
    appliesTo: "both",
    singleOnly: true,
  },
  "quick-copy": {
    id: "quick-copy",
    label: "Copy",
    icon: Copy,
    shortcut: "Mod+C",
    appliesTo: "both",
  },
  cut: {
    id: "cut",
    label: "Cut",
    icon: Scissors,
    shortcut: "Mod+X",
    appliesTo: "task",
  },
  share: {
    id: "share",
    label: "Share",
    icon: Share2,
    appliesTo: "both",
  },
  "convert-to-section": {
    id: "convert-to-section",
    label: "Convert to Section",
    icon: Heading,
    appliesTo: "task",
    singleOnly: true,
  },
  "convert-to-project": {
    id: "convert-to-project",
    label: "Convert to Project",
    // Fallback icon — consumers override with <TimelineIcon type="project"> for the live pie chart
    icon: Project,
    appliesTo: "task",
    singleOnly: true,
  },
  delete: {
    id: "delete",
    label: "Delete",
    labelBulk: "Delete All",
    icon: Trash2,
    appliesTo: "both",
    destructive: true,
  },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get action def by ID — convenient for rendering */
export function getAction(id: TaskActionId): TaskActionDef {
  return TASK_ACTIONS[id]
}

/** Get multiple action defs for a menu section */
export function getActions(ids: TaskActionId[]): TaskActionDef[] {
  return ids.map((id) => TASK_ACTIONS[id])
}
